import type RootState from 'app-components/utils/redux-store/rootState';
import { List } from 'immutable';
import { createSelector } from 'reselect';

import { createAppSelector, createWeakMapSelector } from 'app-components/utils/core/reselect';
import { type BusinessColor, businessColorSequence } from '../../utils/theme';
import { BUSINESS_REDUCER_KEY, type Business } from './businessTypes';

type State = Pick<RootState, typeof BUSINESS_REDUCER_KEY>;
export const getStore = (state: State) => state[BUSINESS_REDUCER_KEY];
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getError = (state: State) => getStore(state).error;
export const getResourcesById = (state: State) => getStore(state).resourcesById;

export const getBusinessForId = createSelector(
  [getResourcesById, (_state: unknown, id: string) => id],
  (businessesById, id) => businessesById.get(id),
);

// TODO: refactor with indices so it supports current palette preference

export const getBusinessColors = createAppSelector([getResourcesById], (businesses) => {
  const colorSet = new Set<BusinessColor>();
  if (businesses.size >= businessColorSequence.length) {
    return businessColorSequence;
  }
  businesses.forEach((business) => {
    if (business.color) {
      colorSet.add(business.color);
    }
  });
  return businessColorSequence.filter((color) => !colorSet.has(color));
});

export const getBusinessNames = createAppSelector([getResourcesById], (businesses) => {
  const nameObj: Record<string, Business> = {};
  businesses.forEach((business) => {
    if (business.name) {
      nameObj[business.name] = business;
    }
  });
  return nameObj;
});

export const getBusinessList = createAppSelector([getResourcesById], (businesses) => {
  const defaultBusiness: Business[] = [];
  const openBusinesses: Business[] = [];
  const closedBusinesses: Business[] = [];

  businesses.forEach((business) => {
    if (business.isDefault) {
      defaultBusiness.push(business);
    } else if (business.isClosed) {
      closedBusinesses.push(business);
    } else {
      openBusinesses.push(business);
    }
  });

  openBusinesses.sort((a, b) => a.createdAt?.localeCompare(b.createdAt ?? '') ?? 0);

  return List(defaultBusiness.concat(openBusinesses).concat(closedBusinesses));
});

export const getOpenBusinesses = createAppSelector([getBusinessList], (businesses) =>
  businesses?.filter((biz) => !biz.isClosed),
);

export const getDefaultBusiness = createAppSelector([getOpenBusinesses], (businesses) => {
  if (!businesses) return null;

  let defaultBusiness = businesses.size === 1 ? businesses.get(0) : null;
  if (!defaultBusiness) {
    defaultBusiness = businesses.find((business) => business.isDefault ?? false);
  }
  if (!defaultBusiness) {
    // OK to sort ISO string date by lexicographical order
    defaultBusiness = businesses.minBy((business) => business.createdAt);
  }

  return defaultBusiness;
});

export const getNonDefaultBusinesses = createAppSelector(
  [getOpenBusinesses, getDefaultBusiness],
  (businesses, defaultBusiness) => {
    if (!businesses || !defaultBusiness) {
      return null;
    }
    return businesses?.filter((business) => business?.id !== defaultBusiness?.id && !business?.isClosed);
  },
);

export const isDefaultBusiness = createWeakMapSelector(
  [getDefaultBusiness, (_state, id: string) => id],
  (defaultBusiness, id) => defaultBusiness?.id === id,
);

export const hasMultipleOpenBusinesses = createAppSelector([getOpenBusinesses], (businesses) => businesses?.size > 1);
